import { sortBy } from "lodash";

import {
  buildDropdownLabelByIdMap,
  formatPhoneLocal,
  isString,
  unique,
  type IDropdownItem,
  type IPassengerInfo,
  type IRideTableDto,
} from "@ryde/shared";

import { RIDE_BULK_PRINT } from "./bulk-operations.rides.constants";
import type { IRidePrintLabels } from "./bulk-print.rides.models";

/** Passengers picked up at the station first, then those dropped off there, each group sorted by unit. */
export const getStationPassengers = ({
  stationId,
  passengersInfo = [],
}: {
  stationId: number;
  passengersInfo: IPassengerInfo[] | undefined;
}): IPassengerInfo[] => [
  ...sortBy(
    passengersInfo.filter(passenger => passenger.from === stationId),
    "unitName",
  ),
  ...sortBy(
    passengersInfo.filter(passenger => passenger.to === stationId),
    "unitName",
  ),
];

/** A flight id with no known code is left out rather than printed as a raw id. */
export const getRideFlightCodes = ({
  flightIds = [],
  flightCodeById,
}: {
  flightIds: number[] | undefined;
  flightCodeById: Map<number, string>;
}): string =>
  unique(
    flightIds.map(flightId => flightCodeById.get(flightId)).filter(isString),
  ).join(RIDE_BULK_PRINT.listDelimiter);

/** Every distinct phone of the passenger in local format, on one line that wraps when it runs long. */
export const getPassengerPhones = (passenger: IPassengerInfo): string =>
  unique(
    [
      formatPhoneLocal(passenger.phone),
      formatPhoneLocal(passenger.phoneAlt),
    ].filter(Boolean),
  ).join(RIDE_BULK_PRINT.listDelimiter);

/** One passenger row: name, phones and seats. The separators stay when the passenger has no phone. */
export const getPassengerPrintLine = (passenger: IPassengerInfo): string =>
  [
    [passenger.firstName, passenger.lastName].filter(Boolean).join(" "),
    getPassengerPhones(passenger),
    `(${passenger.seats})`,
  ].join(RIDE_BULK_PRINT.partsDelimiter);

/** The vehicle type always prints; the assigned vehicle follows it once there is one. */
export const getRidePrintVehicle = ({
  vehicleTypeName,
  vehicleName,
}: Pick<IRideTableDto, "vehicleTypeName" | "vehicleName">): string =>
  [vehicleTypeName, vehicleName]
    .filter(Boolean)
    .join(RIDE_BULK_PRINT.partsDelimiter);

/**
 * Labels the print needs on its first render, built before it mounts. A source that failed to load
 * leaves its labels out instead of blocking the print.
 */
export const buildRidePrintLabels = ({
  areaItems,
  flightItems,
  isFlightsEnabled,
}: {
  areaItems: PromiseSettledResult<IDropdownItem[]>;
  flightItems: PromiseSettledResult<IDropdownItem[]>;
  isFlightsEnabled: boolean;
}): IRidePrintLabels => {
  const labels: IRidePrintLabels = {
    areaNameById: buildDropdownLabelByIdMap(
      areaItems.status === "fulfilled" ? areaItems.value : [],
    ),
    flightCodeById: buildDropdownLabelByIdMap(
      flightItems.status === "fulfilled" ? flightItems.value : [],
    ),
    isFlightsEnabled,
  };

  return labels;
};
